import { Toaster as SonnerToaster, toast } from 'sonner'
import { Toaster } from './components/ui/toaster'
import { TooltipProvider } from './components/ui/tooltip'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
  useParams,
  useNavigate,
} from 'react-router-dom'
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary'
import { lazy, Suspense, useEffect, useState, useRef } from 'react'
import { Loader2 } from 'lucide-react'
import { forceLocalSignOut, supabase } from './lib/supabase'
import { AuthProvider, useAuth } from './contexts/AuthContext'
// import { PasskeyEnrollmentGate } from "./components/auth/PasskeyEnrollmentPrompt";
import { ActiveContextProvider } from './contexts/ActiveContextProvider'
import { ThemeProvider } from './contexts/ThemeContext'
import { AccessibilityProvider } from './contexts/AccessibilityContext'
import { TextToSpeechProvider } from './contexts/TextToSpeechContext'
import { ReadAloudProvider } from './contexts/ReadAloudContext'
import { QASessionProvider } from './contexts/QASessionContext'
import { QAPerspectiveProvider } from './contexts/QAPerspectiveContext'
import { AdminToolbarProvider } from './contexts/AdminToolbarContext'
import { QAPerspectiveLabel } from '@admin/components/qa/QAPerspectiveLabel'
import { useLanguageOverride } from './hooks/useLanguageOverride'
import { debugLog, isAuthDebugEnabled } from './lib/debug'
import { initializeStorage } from './lib/storage'
import { getExtensionAuthSuccessRedirect } from './lib/auth/extension-auth-redirect'
import { resolvePostAuthDestination } from './message-analyzer/lib/guest-coaching-resume'
import { useAdmin } from './hooks/useAdmin'
import { AppFooter } from './components/shared/navigation/AppFooter'
import { AppHeader } from './components/shared/navigation/AppHeader'
import { HomeRedirect } from './components/routing/HomeRedirect'
import { AppRoute } from './components/routing/AppRoute'
import { AnalyzerRouteLayout } from './components/routing/AnalyzerRouteLayout'
import { SettingsRouteLayout } from './components/routing/SettingsRouteLayout'
import { FlowResultsGuard } from './components/routing/FlowResultsGuard'
import { OrgSubRoute } from './components/routing/OrgSubRoute'
import { ProtectedRoute } from './components/routing/ProtectedRoute'
import { FullPageLoading } from './components/routing/RouteLoading'
import { AdminToolbar } from '@admin/components/qa/AdminToolbar'
import { SupportChat } from './components/SupportChat/SupportChat'
import { HelpButton } from './components/help/HelpButton'
import { heatmapTracker } from './lib/heatmap'
import { AccessibilityToolbar } from '@lina/accessibility/components/AccessibilityToolbar'
import { FloatingTTSToolbar } from '@lina/accessibility/components/FloatingTTSToolbar'
import { initializeUserAccessibilitySettings } from './lib/account/user-accessibility'
// AssessorConfigProvider removed - only loaded on admin pages that need it

// Helper to handle lazy loading errors
const lazyWithRetry = (componentImport: () => Promise<any>) =>
  lazy(() =>
    componentImport().catch((error) => {
      // Log full error details for debugging
      console.error('Module loading error:', {
        message: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
      })

      // Check if it's a chunk loading error (stale cache) — reload at most once
      const chunkReloadKey = 'lina-chunk-reload'
      if (
        !sessionStorage.getItem(chunkReloadKey) &&
        (error.message?.includes('Failed to fetch dynamically imported module') ||
          error.message?.includes('Importing a module script failed'))
      ) {
        sessionStorage.setItem(chunkReloadKey, '1')
        toast.error('Update Available', {
          description: 'A new version is available. Reloading page...',
          duration: 2000,
        })
        setTimeout(() => window.location.reload(), 2000)
      } else {
        // Generic error for other issues
        toast.error('Oops! Something went wrong', {
          description: 'The LINA team has been notified. Please try again in a few minutes.',
          duration: 5000,
        })
      }

      throw error
    }),
  )

// --- Core Application Pages ---
// New 3-step analyzer pages
const MessageInputPage = lazyWithRetry(() => import('./message-analyzer/pages/MessageInputPage'))
const AnalysisResultsPage = lazyWithRetry(
  () => import('./message-analyzer/pages/AnalysisResultsPage'),
)
const ResponseDraftPage = lazyWithRetry(() => import('./message-analyzer/pages/ResponseDraftPage'))
const HighRiskPage = lazyWithRetry(() => import('./message-analyzer/pages/HighRiskPage'))
const DemoPage = lazyWithRetry(() => import('./message-analyzer/pages/DemoPage'))
const PartnerUploadPage = lazyWithRetry(() => import('./pages/partner-upload/PartnerUploadPage'))
const ResponseFirstPrototypePage = lazyWithRetry(
  () => import('./pages/prototype/ResponseFirstPrototypePage'),
)
const GrowthDashboardPage = lazyWithRetry(() => import('./pages/user/growth/GrowthDashboardPage'))
const LensInputPage = lazyWithRetry(() => import('./pages/user/lens/LensInputPage'))
const LensResults = lazyWithRetry(() => import('./pages/user/lens/LensResults'))
const LensHistoryPage = lazyWithRetry(() => import('./pages/user/lens/LensHistoryPage'))
const LensReports = lazyWithRetry(() => import('./pages/lens/LensReports'))
import { isAnalyzerPage, restartAnalyzer } from './message-analyzer/lib/analyzer-utils'
import { isLensStandalonePath } from './lib/lens/utils'
import { LensReportLegacyRedirect } from './lib/lens/legacy-report-redirect'
const LoginPage = lazyWithRetry(() => import('./pages/auth/LoginPage'))
const SignupPageNew = lazyWithRetry(() => import('./pages/auth/SignupPageNew'))
const ExtensionLoginPage = lazyWithRetry(() => import('./pages/auth/extension/LoginPage'))
const ExtensionSignupPage = lazyWithRetry(() => import('./pages/auth/extension/SignupPage'))
const VerifyEmailPendingPage = lazyWithRetry(() => import('./pages/auth/VerifyEmailPendingPage'))
const TryLinaPage = lazyWithRetry(() => import('./pages/auth/TryLinaPage'))
const ForgotPasswordPage = lazyWithRetry(() => import('./pages/auth/ForgotPasswordPage'))
const UpdatePasswordPage = lazyWithRetry(() => import('./pages/auth/UpdatePasswordPage'))
const VerifyPage = lazyWithRetry(() => import('./pages/auth/VerifyPage'))
const ConfirmEmailPage = lazyWithRetry(() => import('./pages/auth/ConfirmEmailPage'))
const ChangeEmailPage = lazyWithRetry(() => import('./pages/auth/ChangeEmailPage'))
const EmailConfirmedPage = lazyWithRetry(() => import('./pages/auth/EmailConfirmedPage'))
const AuthSuccessPage = lazyWithRetry(() => import('./pages/auth/extension/AuthSuccessPage'))
const ExtensionInstallPage = lazyWithRetry(() => import('./pages/extension/ExtensionInstallPage'))
const InviteAcceptPage = lazyWithRetry(() => import('./pages/auth/InviteAcceptPage'))
// CALENDAR 2027-01-31: review PartnerFeatureArchivedPage + /partners routes
// MIGRATION:partner-archival expires:2027-01-31
// Reason: Placeholder for archived /partners routes. Delete component + routes
//         together once we're confident no external link/email points to them.
// Owner: @erica
const PartnerFeatureArchivedPage = lazyWithRetry(
  () => import('./pages/errors/PartnerFeatureArchivedPage'),
)
const SettingsIndex = lazyWithRetry(() => import('./pages/user/settings/SettingsIndex'))
const ProfileSettings = lazyWithRetry(() => import('./pages/user/settings/ProfileSettings'))
const ContactProfilesSettings = lazyWithRetry(
  () => import('./pages/user/settings/ContactProfilesSettings'),
)
const AskLinaSettings = lazyWithRetry(() => import('./pages/user/settings/AskLinaSettings'))
const LinaLensSettings = lazyWithRetry(() => import('./pages/user/settings/LinaLensSettings'))
const AnalysisHistoryPage = lazyWithRetry(() => import('./pages/user/history/AnalysisHistoryPage'))
const AccessibilitySettings = lazyWithRetry(
  () => import('./pages/user/settings/AccessibilitySettings'),
)
const SecuritySettings = lazyWithRetry(() => import('./pages/user/settings/SecuritySettings'))
const SupportSettings = lazyWithRetry(() => import('./pages/user/settings/SupportSettings'))
const SupportResourcesPage = lazyWithRetry(() => import('./pages/user/SupportResourcesPage'))
// AnalyticsDashboardPage (the old all-in-one dashboard) was archived to
// admin/_archived/pages/AnalyticsDashboardPage.tsx. The new /admin layout
// replaces it. Same for UserManagement (→ UsersPage), DeletedAccounts,
// CheckInAnalytics, EdgeFunctions (→ per-product Edge Functions tabs),
// SessionMetrics (→ per-product Usage tabs), and ScenarioComparisonView.

const UserAnalyticsPage = lazyWithRetry(() => import('@admin/pages/UserAnalyticsPage'))
const EmailTestingPage = lazyWithRetry(() => import('@admin/pages/EmailTestingPage'))

const SupportTicketsPage = lazyWithRetry(() => import('@admin/pages/SupportTicketsPage'))
const KnowledgeBrowserPage = lazyWithRetry(
  () => import('@admin/pages/knowledge/KnowledgeBrowserPage'),
)
const LinaTestSuitePage = lazyWithRetry(() => import('@admin/pages/testing/LinaTestSuitePage'))
const LensRewriteRunsPage = lazyWithRetry(() => import('@admin/pages/testing/LensRewriteRunsPage'))
const LensVersionCompareRunsPage = lazyWithRetry(
  () => import('@admin/pages/testing/LensVersionCompareRunsPage'),
)
const LensVersionCompareDetailPage = lazyWithRetry(
  () => import('@admin/pages/testing/LensVersionCompareDetailPage'),
)
const LensNextGoldCompareRunsPage = lazyWithRetry(
  () => import('@admin/pages/testing/LensNextGoldCompareRunsPage'),
)
const LensNextGoldCompareDetailPage = lazyWithRetry(
  () => import('@admin/pages/testing/LensNextGoldCompareDetailPage'),
)
const LensPipelineTestPage = lazyWithRetry(
  () => import('@admin/pages/testing/LensPipelineTestPage'),
)
const LensPipelineTestRunPage = lazyWithRetry(
  () => import('@admin/pages/testing/LensPipelineTestRunPage'),
)
const LensExtractionCheckPage = lazyWithRetry(
  () => import('@admin/pages/testing/LensExtractionCheckPage'),
)
const LensContractsPage = lazyWithRetry(() => import('@admin/pages/testing/LensContractsPage'))
const LinaEngineCheckPage = lazyWithRetry(() => import('@admin/pages/testing/LinaEngineCheckPage'))
// Legacy profile-config / preflight / content-type pages removed — routes redirect below.
const TestRunsPage = lazyWithRetry(() => import('@admin/pages/testing/TestRunsPage'))
const TestRunPage = lazyWithRetry(() => import('@admin/pages/testing/TestRunPage'))
const TestRunDetailPage = lazyWithRetry(() => import('@admin/pages/testing/TestRunDetailPage'))
const LensTestLibraryPage = lazyWithRetry(
  () => import('@admin/pages/testing/library/LensTestLibraryPage'),
)
const LensTestDocumentPage = lazyWithRetry(
  () => import('@admin/pages/testing/library/LensTestDocumentPage'),
)
const LensRegressionDashboardPage = lazyWithRetry(
  () => import('@admin/pages/testing/regression/LensRegressionDashboardPage'),
)
const LensRegressionRunsPage = lazyWithRetry(
  () => import('@admin/pages/testing/regression/LensRegressionRunsPage'),
)
const LensRegressionRunPage = lazyWithRetry(
  () => import('@admin/pages/testing/regression/LensRegressionRunPage'),
)
const LensRegressionComparePage = lazyWithRetry(
  () => import('@admin/pages/testing/regression/LensRegressionComparePage'),
)
const LensDocumentHistoryPage = lazyWithRetry(
  () => import('@admin/pages/testing/LensDocumentHistoryPage'),
)
const LensShadowDiffsPage = lazyWithRetry(
  () => import('@admin/pages/testing/_legacy/LensShadowDiffsPage'),
)
const PostFlipDriftPage = lazyWithRetry(() => import('@admin/pages/testing/PostFlipDriftPage'))
const CharacteristicAccuracyPage = lazyWithRetry(
  () => import('@admin/pages/testing/CharacteristicAccuracyPage'),
)

// Response testing now integrated into TestResultsPage

// Error Pages
const NotFoundPage = lazyWithRetry(() => import('./pages/errors/NotFoundPage'))
const ServerErrorPage = lazyWithRetry(() => import('./pages/errors/ServerErrorPage'))
const AccountErrorPage = lazyWithRetry(() => import('./pages/errors/AccountErrorPage'))
const LoadFailedPage = lazyWithRetry(() => import('./pages/errors/LoadFailedPage'))
const MaintenancePage = lazyWithRetry(() => import('./pages/errors/MaintenancePage'))

const AdminSettingsPage = lazyWithRetry(() => import('@admin/pages/AdminSettingsPage'))
const ChromeExtensionManagementPage = lazyWithRetry(
  () => import('@admin/pages/ChromeExtensionManagementPage'),
)
// SessionMetricsPage archived → replaced by per-product Usage tabs.

// --- Admin shell (redesigned dashboard with sidebar + nested product pages) ---
const AdminLayout = lazyWithRetry(() => import('@admin/components/shared/AdminLayout'))
const AdminOverviewPage = lazyWithRetry(() => import('@admin/pages/AdminOverviewPage'))
const AskLinaPage = lazyWithRetry(() => import('@admin/pages/lina/AskLinaPage'))
const LensPage = lazyWithRetry(() => import('@admin/pages/lina/LensPage'))
const MCPPage = lazyWithRetry(() => import('@admin/pages/lina/MCPPage'))
const AssessmentRequestsPage = lazyWithRetry(
  () => import('@admin/pages/lina/AssessmentRequestsPage'),
)
const LensTrialPage = lazyWithRetry(() => import('@admin/pages/lina/LensTrialPage'))
const LensAssessmentDetailPage = lazyWithRetry(
  () => import('@admin/pages/lina/LensAssessmentDetailPage'),
)
const LensConsistencyTesterPage = lazyWithRetry(
  () => import('@admin/pages/lina/LensConsistencyTesterPage'),
)
const ObservatoryPage = lazyWithRetry(() => import('@admin/pages/lina/observatory/ObservatoryPage'))
const ObservatoryDetailPage = lazyWithRetry(
  () => import('@admin/pages/lina/observatory/ObservatoryDetailPage'),
)
const ExtensionPage = lazyWithRetry(() => import('@admin/pages/lina/ExtensionPage'))
const UsersPage = lazyWithRetry(() => import('@admin/pages/UsersPage'))

const SettingsPage = lazyWithRetry(() => import('@admin/pages/SettingsPage'))
const AdminOrganizationsPage = lazyWithRetry(
  () => import('@admin/pages/organizations/AdminOrganizationsPage'),
)
const AdminPilotOrganizationsPage = lazyWithRetry(
  () => import('@admin/pages/organizations/AdminPilotOrganizationsPage'),
)
const AdminOrganizationDetailPage = lazyWithRetry(
  () => import('@admin/pages/organizations/AdminOrganizationDetailPage'),
)
const PilotBatchUploadPage = lazyWithRetry(
  () => import('@admin/pages/organizations/pilot/PilotBatchUploadPage'),
)
const PilotDocumentRunPage = lazyWithRetry(
  () => import('@admin/pages/organizations/pilot/PilotDocumentRunPage'),
)

const TaskListPage = lazyWithRetry(() => import('@admin/pages/TaskListPage'))
const DesignSystemPage = lazyWithRetry(() => import('@admin/pages/knowledge/DesignSystemPage'))

const BlogListPage = lazyWithRetry(() => import('@admin/pages/knowledge/blog/BlogListPage'))
const BlogEditorPage = lazyWithRetry(() => import('@admin/pages/knowledge/blog/BlogEditorPage'))
const RegistryLegacyRedirect = lazyWithRetry(
  () => import('@admin/components/registry/RegistryLegacyRedirect'),
)
const RegistryOrganizationsPage = lazyWithRetry(
  () => import('@admin/pages/registry/RegistryOrganizationsPage'),
)
const RegistryOrganizationDetailPage = lazyWithRetry(
  () => import('@admin/pages/registry/RegistryOrganizationDetailPage'),
)
const RegistryDashboardPage = lazyWithRetry(
  () => import('@admin/pages/registry/RegistryDashboardPage'),
)
const RegistryRunsPage = lazyWithRetry(() => import('@admin/pages/registry/RegistryRunsPage'))
const RegistryOperationDetailPage = lazyWithRetry(
  () => import('@admin/pages/registry/RegistryOperationDetailPage'),
)
const RegistryRunDetailPage = lazyWithRetry(
  () => import('@admin/pages/registry/RegistryRunDetailPage'),
)
const RegistryIntakeReviewPage = lazyWithRetry(
  () => import('@admin/pages/registry/RegistryIntakeReviewPage'),
)
const RegistryAcquisitionSourcePage = lazyWithRetry(
  () => import('@admin/pages/registry/RegistryAcquisitionSourcePage'),
)
const RegistryAcquisitionRunPage = lazyWithRetry(
  () => import('@admin/pages/registry/RegistryAcquisitionRunPage'),
)
const RegistryAcquisitionPage = lazyWithRetry(
  () => import('@admin/pages/registry/RegistryAcquisitionPage'),
)
const RegistryCohortsPage = lazyWithRetry(() => import('@admin/pages/registry/RegistryCohortsPage'))
const RegistryCohortDetailPage = lazyWithRetry(
  () => import('@admin/pages/registry/RegistryCohortDetailPage'),
)
const RegistryReportsPage = lazyWithRetry(() => import('@admin/pages/registry/RegistryReportsPage'))
const RegistryReportPreviewPage = lazyWithRetry(
  () => import('@admin/pages/registry/RegistryReportPreviewPage'),
)
const RegistryPageTargetsPage = lazyWithRetry(
  () => import('@admin/pages/registry/RegistryPageTargetsPage'),
)
const ScoringWeightMatrixPage = lazyWithRetry(
  () => import('@admin/pages/scoring/ScoringWeightMatrixPage'),
)
const ScoringAuditLogPage = lazyWithRetry(() => import('@admin/pages/scoring/ScoringAuditLogPage'))
const PartnerApplicationsPage = lazyWithRetry(
  () => import('@admin/pages/organizations/PartnerApplicationsPage'),
)

// --- Research Studio ---
const ResearchDashboardPage = lazyWithRetry(
  () => import('@admin/pages/research/ResearchDashboardPage'),
)
const ResearchShell = lazyWithRetry(() => import('@admin/pages/research/discovery/ResearchShell'))
const DiscoveryInboxPage = lazyWithRetry(
  () => import('@admin/pages/research/discovery/DiscoveryInboxPage'),
)
const DiscoveryTopicsPage = lazyWithRetry(
  () => import('@admin/pages/research/discovery/DiscoveryTopicsPage'),
)
const DiscoverySettingsPage = lazyWithRetry(
  () => import('@admin/pages/research/discovery/DiscoverySettingsPage'),
)
const DiscoveryRunsPage = lazyWithRetry(
  () => import('@admin/pages/research/discovery/DiscoveryRunsPage'),
)
const ResearchSourcesPage = lazyWithRetry(() => import('@admin/pages/research/ResearchSourcesPage'))
const ResearchImportPage = lazyWithRetry(() => import('@admin/pages/research/ResearchImportPage'))
const ResearchSourceDetailPage = lazyWithRetry(
  () => import('@admin/pages/research/ResearchSourceDetailPage'),
)
const ResearchValidationPage = lazyWithRetry(
  () => import('@admin/pages/research/ResearchValidationPage'),
)
const ResearchConceptDetailPage = lazyWithRetry(
  () => import('@admin/pages/research/ResearchConceptDetailPage'),
)
const ResearchHypothesesPage = lazyWithRetry(
  () => import('@admin/pages/research/ResearchHypothesesPage'),
)
const ResearchHypothesisDetailPage = lazyWithRetry(
  () => import('@admin/pages/research/ResearchHypothesisDetailPage'),
)
const ResearchGapDetailPage = lazyWithRetry(
  () => import('@admin/pages/research/ResearchGapDetailPage'),
)
const ClaimsPage = lazyWithRetry(() => import('@admin/pages/research/ClaimsPage'))
const ClaimDetailPage = lazyWithRetry(() => import('@admin/pages/research/ClaimDetailPage'))

function ResearchRedirect({
  to,
}: {
  to: (params: Readonly<Record<string, string | undefined>>) => string
}) {
  const params = useParams()
  const location = useLocation()
  const target = to(params)
  const join = location.search && !target.includes('?') ? `${target}${location.search}` : target
  return <Navigate to={join} replace />
}

/** Keep old saved/bookmarked Lens Next URLs working while one results route owns dispatch. */
function LensNextResultsRedirect() {
  const location = useLocation()
  const query = new URLSearchParams(location.search)
  query.set('pipeline', 'lens-next')
  return (
    <Navigate
      replace
      to={`/lens/results?${query.toString()}`}
      state={{
        ...((location.state as Record<string, unknown> | null) ?? {}),
        pipeline: 'lens-next',
      }}
    />
  )
}

// --- Communication Preference Study ---
const StudyDashboardAdminPage = lazyWithRetry(() => import('@admin/pages/study/StudyDashboardPage'))
const StudyExamplesAdminPage = lazyWithRetry(() => import('@admin/pages/study/StudyExamplesPage'))
const StudyExampleEditorAdminPage = lazyWithRetry(
  () => import('@admin/pages/study/StudyExampleEditorPage'),
)
const StudyAnalyticsAdminPage = lazyWithRetry(() => import('@admin/pages/study/StudyAnalyticsPage'))

const CheckInDebugPage = lazyWithRetry(() => import('./pages/debug/CheckInDebugPage'))
const OnboardingPageNew = lazyWithRetry(() => import('./pages/user/OnboardingPageNew'))

// --- Org pages (corporate/company accounts) ---
const OrgLayout = lazyWithRetry(() => import('./pages/org/OrgLayout'))
const OrgDashboardPage = lazyWithRetry(() => import('./pages/org/OrgDashboardPage'))
const OrgMembersPage = lazyWithRetry(() => import('./pages/org/OrgMembersPage'))
const OrgSettingsPage = lazyWithRetry(() => import('./pages/org/OrgSettingsPage'))
const OrgBillingPage = lazyWithRetry(() => import('./pages/org/OrgBillingPage'))

// --- Loading component for Suspense ---
const LoadingFallback = FullPageLoading

// Simple error fallback - uses LoadFailedPage component
const ErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  const errorMessage = error instanceof Error ? error.message : ''

  // Don't show error screen for navigation/auth errors - these are transient
  const isNavigationError =
    errorMessage.includes('Navigation') ||
    errorMessage.includes('Cannot update during render') ||
    errorMessage.includes('useNavigate') ||
    errorMessage.includes('useLocation')

  if (isNavigationError) {
    console.warn('Navigation error caught and recovered:', errorMessage)
    setTimeout(() => {
      try {
        resetErrorBoundary()
      } catch {
        window.location.reload()
      }
    }, 100)
    return <LoadingFallback />
  }

  // Use the branded LoadFailedPage component
  return (
    <Suspense fallback={<LoadingFallback />}>
      <LoadFailedPage error={error} resourceName="page" />
    </Suspense>
  )
}

// --- Public Route Guard (redirects authenticated users) ---
const AdminRoute = ({
  children,
  skipOnboardingCheck = true,
}: {
  children: React.ReactNode
  skipOnboardingCheck?: boolean
}) => {
  const { isAdmin, isLoading: adminLoading } = useAdmin()
  const navigate = useNavigate()

  useEffect(() => {
    if (!adminLoading && !isAdmin) {
      navigate('/', { replace: true })
    }
  }, [adminLoading, isAdmin, navigate])

  if (adminLoading) {
    return <LoadingFallback />
  }

  if (!isAdmin) return null

  return <ProtectedRoute skipOnboardingCheck={skipOnboardingCheck}>{children}</ProtectedRoute>
}

// --- Public Route Guard (redirects authenticated users) ---
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isLoading, session, accountId, isAccountLoading, isAnonymous } = useAuth()
  const location = useLocation()
  const [inviteFlowReady, setInviteFlowReady] = useState(false)

  const searchParams = new URLSearchParams(location.search)
  const inviteToken = searchParams.get('token')
  const isInvitePage = location.pathname.includes('/auth/invite')
  const needsInviteSignOut = Boolean(isInvitePage && inviteToken)

  useEffect(() => {
    if (!needsInviteSignOut) {
      setInviteFlowReady(true)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        if (isAuthDebugEnabled())
          console.log('[PublicRoute] Invite link detected, clearing any existing session')
        localStorage.clear()
        sessionStorage.clear()
        await forceLocalSignOut()
      } catch (error) {
        console.error('[PublicRoute] Invite sign-out error:', error)
      } finally {
        if (!cancelled) setInviteFlowReady(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [needsInviteSignOut, location.pathname, location.search])

  // Wait for auth state and account resolution before redirecting from auth pages.
  // This prevents login<->app ping-pong when session exists but account lookup is still in flight.
  const waiting = needsInviteSignOut
    ? !inviteFlowReady
    : isLoading || (!!session && isAccountLoading)

  if (waiting) {
    return <LoadingFallback />
  }

  const isAuthenticated = !!session
  const hasResolvedAccount = !!accountId
  const extensionAuthRedirect = getExtensionAuthSuccessRedirect({
    pathname: location.pathname,
    search: location.search,
    isAuthenticated,
    hasResolvedAccount,
  })

  if (isAuthDebugEnabled()) {
    console.log('[PublicRoute] Session check:', {
      path: location.pathname,
      hasSession: isAuthenticated,
      sessionUser: session?.user?.email,
    })
  }

  if (extensionAuthRedirect) {
    if (isAuthDebugEnabled()) {
      console.log('[PublicRoute] Redirecting extension auth flow to:', extensionAuthRedirect)
    }
    return <Navigate to={extensionAuthRedirect} replace />
  }

  // Redirect authenticated users to app (except for update-password and invite pages).
  // Anonymous guests may stay on signup to link a real identity (e.g. explore coaching gate).
  const isConfirmEmailPage = location.pathname === '/auth/confirm-email'
  const isAuthSuccessPage =
    location.pathname === '/auth/success' || location.pathname === '/extension-success'
  const isExtensionInstallPage = location.pathname === '/extension/install'
  const isGuestAuthPage =
    location.pathname === '/auth/signup' ||
    location.pathname === '/auth/extension/signup' ||
    location.pathname === '/auth/login' ||
    location.pathname === '/auth/extension/login' ||
    location.pathname === '/auth/try'
  if (
    isAuthenticated &&
    !(isAnonymous && isGuestAuthPage) &&
    !location.pathname.includes('/auth/update-password') &&
    !isConfirmEmailPage &&
    !isAuthSuccessPage &&
    !isExtensionInstallPage &&
    !isInvitePage
  ) {
    if (isAuthDebugEnabled())
      console.log('[PublicRoute] Redirecting authenticated user from:', location.pathname)
    return <Navigate to={resolvePostAuthDestination('/')} replace />
  }

  if (isAuthDebugEnabled()) console.log('[PublicRoute] Allowing access to:', location.pathname)
  return <>{children}</>
}

// --- App Shell (page background + layout wrapper for header, main, footer) ---
function isStandaloneSurface(pathname: string): boolean {
  return (
    pathname.startsWith('/auth/') ||
    pathname.startsWith('/partner-upload/') ||
    isLensStandalonePath(pathname) ||
    pathname === '/extension-auth' ||
    pathname === '/extension-success' ||
    pathname === '/extension/install'
  )
}

const AppShell = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation()
  const isStandaloneRoute = isStandaloneSurface(location.pathname)

  return (
    <div
      className={`flex flex-col min-h-screen min-w-screen ${!isStandaloneRoute ? 'app-page-bg' : 'bg-background'} ${!isStandaloneRoute ? 'pt-16' : ''}`}
      role="document"
    >
      <nav aria-label="Skip links" data-skip-read-aloud="true">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-0 focus:left-0 focus:z-[9999] focus:p-4 focus:bg-primary focus:text-primary-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          data-skip-read-aloud="true"
        >
          Skip to main content
        </a>
      </nav>
      {children}
    </div>
  )
}

// --- Main App Content Component ---
const AppContent = () => {
  // Apply language override from admin settings
  useLanguageOverride()

  // Initialize heatmap tracking (privacy-safe)
  useEffect(() => {
    heatmapTracker.init()
    return () => heatmapTracker.destroy()
  }, [])

  // Initialize user accessibility settings from cookies
  useEffect(() => {
    initializeUserAccessibilitySettings()
  }, [])

  const location = useLocation()

  const isAnalyzerRoute =
    location.pathname.startsWith('/analyze') ||
    location.pathname === '/demo' ||
    location.pathname === '/settings' ||
    location.pathname === '/admin/lina-v2-user-results' ||
    location.pathname === '/admin/lina-v2-user-results-output'

  return (
    <TooltipProvider>
      <main
        id="main-content"
        tabIndex={-1}
        className={`relative min-h-0 flex-grow ${isAnalyzerRoute ? 'flex flex-col' : ''}`}
      >
        <Suspense fallback={<LoadingFallback />}>
          <div className={isAnalyzerRoute ? 'flex min-h-0 flex-1 flex-col' : 'contents'}>
            <Routes>
              {/* --- Public Auth Routes --- */}
              {/* Login page */}
              <Route
                path="/auth/login"
                element={
                  <PublicRoute>
                    <LoginPage />
                  </PublicRoute>
                }
              />
              {/* Signup page (email → password or verification) */}
              <Route
                path="/auth/signup"
                element={
                  <PublicRoute>
                    <SignupPageNew />
                  </PublicRoute>
                }
              />
              <Route
                path="/auth/extension/login"
                element={
                  <PublicRoute>
                    <ExtensionLoginPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/extension-auth"
                element={
                  <PublicRoute>
                    <Navigate to="/auth/extension/login" replace />
                  </PublicRoute>
                }
              />
              <Route
                path="/auth/extension/signup"
                element={
                  <PublicRoute>
                    <ExtensionSignupPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/auth/try"
                element={
                  <PublicRoute>
                    <TryLinaPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/auth/verify-email-pending"
                element={
                  <PublicRoute>
                    <VerifyEmailPendingPage />
                  </PublicRoute>
                }
              />
              <Route path="/verify-email" element={<Navigate to="/auth/confirm-email" replace />} />
              <Route
                path="/auth/forgot-password"
                element={
                  <PublicRoute>
                    <ForgotPasswordPage />
                  </PublicRoute>
                }
              />
              <Route path="/auth/update-password" element={<UpdatePasswordPage />} />
              {/* Handle Supabase password reset URL variations */}
              <Route path="/auth/updatepasswordpage" element={<UpdatePasswordPage />} />
              <Route path="/auth/auth/update-password" element={<UpdatePasswordPage />} />
              <Route path="/auth/auth/updatepasswordpage" element={<UpdatePasswordPage />} />
              <Route path="/auth/invite" element={<Navigate to="/login" replace />} />
              <Route
                path="/auth/partner-invite"
                element={
                  <PublicRoute>
                    <InviteAcceptPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/auth/partner-member-invite"
                element={
                  <PublicRoute>
                    <InviteAcceptPage />
                  </PublicRoute>
                }
              />

              <Route path="/auth/verify" element={<VerifyPage />} />
              <Route path="/auth/confirm-email" element={<ConfirmEmailPage />} />
              <Route path="/auth/success" element={<AuthSuccessPage />} />
              <Route path="/extension-success" element={<AuthSuccessPage />} />
              <Route
                path="/extension/install"
                element={
                  <PublicRoute>
                    <ExtensionInstallPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/auth/change-email"
                element={
                  <ProtectedRoute>
                    <ChangeEmailPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/auth/email-confirmed" element={<EmailConfirmedPage />} />

              {/* --- Public demo (no auth required; same UI as /analyze results) --- */}
              <Route path="/demo" element={<DemoPage />} />
              {/* --- Partner Pilot Document Upload Portal (no auth, token-gated) --- */}
              <Route path="/partner-upload/:token" element={<PartnerUploadPage />} />
              {/* --- Response-first spike (isolated prototype, no auth) --- */}
              <Route path="/prototype/response-first" element={<ResponseFirstPrototypePage />} />

              {/* --- Onboarding Route (Protected but no onboarding check) --- */}
              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute skipOnboardingCheck>
                    <OnboardingPageNew />
                  </ProtectedRoute>
                }
              />

              {/* --- Protected Routes --- */}
              <Route
                path="/"
                element={
                  <AppRoute routeId="home">
                    <HomeRedirect />
                  </AppRoute>
                }
              />
              <Route element={<AnalyzerRouteLayout />}>
                <Route path="/analyze" element={<MessageInputPage />} />
                <Route
                  path="/analyze/results"
                  element={
                    <FlowResultsGuard flow="analyzer">
                      <AnalysisResultsPage />
                    </FlowResultsGuard>
                  }
                />
                <Route
                  path="/analyze/response"
                  element={
                    <ProtectedRoute>
                      <ResponseDraftPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/analyze/high-risk" element={<HighRiskPage />} />
              </Route>
              <Route
                path="/lens"
                element={
                  <AppRoute routeId="lens">
                    <LensInputPage />
                  </AppRoute>
                }
              />
              <Route
                path="/lens/results"
                element={
                  <AppRoute routeId="lens">
                    <FlowResultsGuard flow="lens">
                      <LensResults />
                    </FlowResultsGuard>
                  </AppRoute>
                }
              />
              <Route
                path="/lens/next/results"
                element={
                  <AppRoute routeId="lens">
                    <LensNextResultsRedirect />
                  </AppRoute>
                }
              />
              <Route
                path="/lens/history"
                element={
                  <AppRoute routeId="lens">
                    <LensHistoryPage />
                  </AppRoute>
                }
              />
              {/* Tokenized B2B audit reports — public, no login (?token= from email) */}
              <Route path="/lens/report" element={<LensReportLegacyRedirect />} />
              <Route
                path="/lens/reports/:reportId"
                element={
                  <AppRoute routeId="lens-report">
                    <LensReports />
                  </AppRoute>
                }
              />
              <Route
                path="/lens/pilot-reports/:reportId"
                element={
                  <AppRoute routeId="lens-report">
                    <LensReports />
                  </AppRoute>
                }
              />
              {/* Explore UI archived — soft redirect to Ask LINA analyzer */}
              <Route path="/explore/*" element={<Navigate to="/analyze" replace />} />
              <Route path="/explore" element={<Navigate to="/analyze" replace />} />
              <Route
                path="/growth"
                element={
                  <AppRoute routeId="growth">
                    <GrowthDashboardPage />
                  </AppRoute>
                }
              />
              <Route path="/account" element={<Navigate to="/settings" replace />} />
              <Route element={<SettingsRouteLayout />}>
                <Route path="/settings" element={<SettingsIndex />} />
                <Route path="/settings/profile" element={<ProfileSettings />} />
                <Route path="/settings/contacts" element={<ContactProfilesSettings />} />
                <Route path="/settings/accessibility" element={<AccessibilitySettings />} />
                <Route path="/settings/security" element={<SecuritySettings />} />
                <Route path="/settings/support" element={<SupportSettings />} />
                <Route path="/settings/ask-lina" element={<AskLinaSettings />} />
                <Route path="/settings/lina-lens" element={<LinaLensSettings />} />
              </Route>
              <Route
                path="/history"
                element={
                  <AppRoute routeId="history">
                    <AnalysisHistoryPage />
                  </AppRoute>
                }
              />
              <Route
                path="/support"
                element={
                  <ProtectedRoute>
                    <SupportResourcesPage />
                  </ProtectedRoute>
                }
              />
              {/* --- Organization Routes (corporate/company accounts) --- */}
              <Route
                path="/org"
                element={
                  <AppRoute
                    routeId="org"
                    autoSwitchOrgContext
                    authMode="session"
                    skipOnboardingCheck
                  >
                    <OrgLayout />
                  </AppRoute>
                }
              >
                <Route index element={<Navigate to="/org/dashboard" replace />} />
                <Route path="dashboard" element={<OrgDashboardPage />} />
                <Route
                  path="members"
                  element={
                    <OrgSubRoute path="/org/members">
                      <OrgMembersPage />
                    </OrgSubRoute>
                  }
                />
                <Route
                  path="settings"
                  element={
                    <OrgSubRoute path="/org/settings">
                      <OrgSettingsPage />
                    </OrgSubRoute>
                  }
                />
                <Route
                  path="billing"
                  element={
                    <OrgSubRoute path="/org/billing">
                      <OrgBillingPage />
                    </OrgSubRoute>
                  }
                />
              </Route>

              {/* Partner Routes */}
              <Route
                path="/partners"
                element={
                  <ProtectedRoute skipOnboardingCheck>
                    <PartnerFeatureArchivedPage />
                  </ProtectedRoute>
                }
              />
              {/*
                Legacy /admin dashboard (AnalyticsDashboardPage + dashboard-tabs)
                was archived to admin/_archived/. The redesigned dashboard at
                /admin replaces it. Restore from /admin/_archived/pages/
                AnalyticsDashboardPage.tsx if needed.
              */}
              <Route
                path="/admin/user-analytics/:userId"
                element={
                  <AdminRoute>
                    <UserAnalyticsPage />
                  </AdminRoute>
                }
              />
              {/* /admin/user-management archived → use /admin/users. */}
              <Route
                path="/admin/partner-management"
                element={
                  <AdminRoute>
                    <Navigate to="/admin/organizations" replace />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/partners/:orgId"
                element={
                  <AdminRoute>
                    <AdminPartnersLegacyRedirect />
                  </AdminRoute>
                }
              />
              {/*
                /admin/deleted-accounts, /admin/checkin-analytics, and
                /admin/edge-functions archived. Per-product Edge Functions
                tabs replace the latter; the others were legacy-dashboard tiles.

                Previously the pages below (email-testing, support-tickets,
                knowledge, tasks, design-system, blog,
                testing, dashboard-controls, chrome-extension) were sibling
                routes of /admin and rendered without the sidebar. They now
                live as CHILDREN of /admin under <AdminLayout /> so they
                inherit the sidebar and unified header.
              */}

              <Route path="/debug/checkins" element={<CheckInDebugPage />} />
              {/* /admin/comparison/:scenarioId archived. */}
              {/* /admin/session-metrics archived → per-product Usage tabs. */}

              {/*
                --- Admin dashboard ---
                /admin renders the persistent-sidebar layout. Child product
                pages (ask-lina, lens, lens-trial, extension, users, operations,
                settings) render via <Outlet />.

                Existing /admin/* sibling routes above (user-management,
                qa-sessions, etc.) continue to resolve as standalone pages
                since they don't have a nested child entry here.
                The old all-in-one dashboard now lives at /admin/legacy-dashboard.
              */}
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminLayout />
                  </AdminRoute>
                }
              >
                <Route index element={<AdminOverviewPage />} />
                <Route path="ask-lina" element={<AskLinaPage />} />
                <Route path="lens" element={<LensPage />} />
                <Route
                  path="lens/assessment/:assessmentId"
                  element={<LensAssessmentDetailPage />}
                />
                {/* Plural alias: pilot org links historically used /assessments/ */}
                <Route
                  path="lens/assessments/:assessmentId"
                  element={<LensAssessmentDetailPage />}
                />
                <Route path="lens/consistency-tester" element={<LensConsistencyTesterPage />} />
                <Route path="lens-test" element={<Navigate to="/admin/study/examples" replace />} />
                <Route path="lens-mcp" element={<MCPPage />} />
                <Route path="assessment-requests" element={<AssessmentRequestsPage />} />
                <Route path="lens-trial" element={<LensTrialPage />} />
                <Route path="extension" element={<ExtensionPage />} />
                <Route path="users" element={<UsersPage />} />
                <Route path="operations" element={<Navigate to="/admin" replace />} />
                <Route path="cron-jobs" element={<Navigate to="/admin" replace />} />
                <Route path="organizations" element={<AdminOrganizationsPage />} />
                <Route path="organizations/pilots" element={<AdminPilotOrganizationsPage />} />
                <Route path="organizations/:orgId" element={<AdminOrganizationDetailPage />} />
                <Route
                  path="organizations/:orgId/pilot/upload"
                  element={<PilotBatchUploadPage />}
                />
                <Route
                  path="organizations/:orgId/pilot/documents/:documentId/run"
                  element={<PilotDocumentRunPage />}
                />
                <Route path="settings" element={<SettingsPage />} />

                {/* Previously sibling routes — now children so they inherit AdminLayout's sidebar */}
                <Route path="email-testing" element={<EmailTestingPage />} />
                <Route path="observatory" element={<ObservatoryPage />} />
                <Route path="observatory/:analysisId" element={<ObservatoryDetailPage />} />
                <Route path="support-tickets" element={<SupportTicketsPage />} />
                <Route path="knowledge" element={<KnowledgeBrowserPage />} />
                <Route path="tasks" element={<TaskListPage />} />
                <Route path="design-system" element={<DesignSystemPage />} />
                <Route path="blog" element={<BlogListPage />} />
                <Route path="blog/:id" element={<BlogEditorPage />} />
                <Route path="testing" element={<LinaTestSuitePage />} />
                <Route path="testing/rewrite" element={<LensRewriteRunsPage />} />
                {/* lens-v6 vs lens-v7 side-by-side compare */}
                <Route path="testing/version-compare" element={<LensVersionCompareRunsPage />} />
                <Route
                  path="testing/version-compare/:runId"
                  element={<LensVersionCompareDetailPage />}
                />
                <Route path="testing/lens-next-compare" element={<LensNextGoldCompareRunsPage />} />
                <Route
                  path="testing/lens-next-compare/:runId"
                  element={<LensNextGoldCompareDetailPage />}
                />
                <Route path="testing/pipeline" element={<LensPipelineTestPage />} />
                <Route path="testing/pipeline/:runId" element={<LensPipelineTestRunPage />} />
                <Route path="testing/extraction-check" element={<LensExtractionCheckPage />} />
                <Route path="testing/lina-engine-check" element={<LinaEngineCheckPage />} />
                <Route path="testing/contracts" element={<LensContractsPage />} />
                <Route
                  path="testing/profile-config"
                  element={<Navigate to="/admin/testing/contracts" replace />}
                />
                <Route
                  path="testing/preflight"
                  element={<Navigate to="/admin/testing/contracts" replace />}
                />
                <Route
                  path="testing/content-type"
                  element={<Navigate to="/admin/testing" replace />}
                />
                <Route path="testing/history" element={<LensDocumentHistoryPage />} />
                <Route path="testing/library" element={<LensTestLibraryPage />} />
                <Route path="testing/library/:documentId" element={<LensTestDocumentPage />} />
                <Route path="testing/regression" element={<LensRegressionDashboardPage />} />
                <Route path="testing/regression/runs" element={<LensRegressionRunsPage />} />
                <Route path="testing/regression/runs/:runId" element={<LensRegressionRunPage />} />
                <Route path="testing/regression/compare" element={<LensRegressionComparePage />} />
                <Route path="testing/shadow-diffs" element={<LensShadowDiffsPage />} />
                <Route path="testing/post-flip-drift" element={<PostFlipDriftPage />} />
                <Route
                  path="testing/characteristic-accuracy"
                  element={<CharacteristicAccuracyPage />}
                />
                <Route path="testing/runs" element={<TestRunsPage />} />
                <Route path="testing/run/:batchId" element={<TestRunPage />} />
                <Route path="testing/case/:runId" element={<TestRunDetailPage />} />
                <Route path="dashboard-controls" element={<AdminSettingsPage />} />
                <Route path="chrome-extension" element={<ChromeExtensionManagementPage />} />
                <Route path="registry" element={<RegistryOrganizationsPage />} />
                <Route path="registry/dashboard" element={<RegistryDashboardPage />} />
                <Route
                  path="registry/organizations/:id"
                  element={<RegistryOrganizationDetailPage />}
                />
                <Route path="registry/cohorts" element={<RegistryCohortsPage />} />
                <Route path="registry/cohorts/:id" element={<RegistryCohortDetailPage />} />
                <Route path="registry/runs" element={<RegistryRunsPage />} />
                <Route
                  path="registry/runs/op/:source/:sourceId"
                  element={<RegistryOperationDetailPage />}
                />
                <Route path="registry/runs/:id" element={<RegistryRunDetailPage />} />
                <Route path="registry/review" element={<RegistryIntakeReviewPage />} />
                <Route path="registry/intake-review" element={<RegistryLegacyRedirect />} />
                <Route path="registry/acquisition" element={<RegistryAcquisitionPage />} />
                <Route
                  path="registry/acquisition/sources/:id"
                  element={<RegistryAcquisitionSourcePage />}
                />
                <Route
                  path="registry/acquisition/runs/:id"
                  element={<RegistryAcquisitionRunPage />}
                />
                <Route path="registry/overview" element={<RegistryLegacyRedirect />} />
                <Route path="registry/companies" element={<RegistryLegacyRedirect />} />
                <Route path="registry/companies/:id" element={<RegistryLegacyRedirect />} />
                <Route path="registry/pipeline" element={<RegistryLegacyRedirect />} />
                <Route
                  path="registry/settings"
                  element={<Navigate to="/admin/registry/dashboard" replace />}
                />
                <Route path="registry/reports" element={<RegistryReportsPage />} />
                <Route path="registry/reports/preview" element={<RegistryReportPreviewPage />} />
                <Route path="registry/page-targets" element={<RegistryPageTargetsPage />} />
                <Route path="scoring/weights" element={<ScoringWeightMatrixPage />} />
                <Route path="scoring/audit-log" element={<ScoringAuditLogPage />} />
                <Route path="partner-applications" element={<PartnerApplicationsPage />} />

                {/* Research Studio — canonical IA routes (2026-08-13) */}
                <Route path="research" element={<ResearchDashboardPage />} />

                {/* Research shell: Programs (default) | Inbox | Activity | Settings */}
                <Route path="research/research" element={<ResearchShell />}>
                  <Route index element={<Navigate to="programs" replace />} />
                  <Route path="programs" element={<DiscoveryTopicsPage />} />
                  <Route path="inbox" element={<DiscoveryInboxPage />} />
                  <Route path="activity" element={<DiscoveryRunsPage />} />
                  <Route path="settings" element={<DiscoverySettingsPage />} />
                </Route>

                {/* Papers corpus */}
                <Route path="research/papers" element={<ResearchSourcesPage />} />
                <Route path="research/papers/import" element={<ResearchImportPage />} />
                <Route path="research/papers/:id" element={<ResearchSourceDetailPage />} />
                <Route
                  path="research/papers/:id/validate/:pass"
                  element={<ResearchValidationPage />}
                />

                {/* Claims (published research-finding + pending drafts) */}
                <Route path="research/claims" element={<ClaimsPage />} />
                <Route path="research/claims/:id" element={<ClaimDetailPage />} />

                <Route path="research/questions" element={<ResearchHypothesesPage />} />
                <Route path="research/questions/:id" element={<ResearchHypothesisDetailPage />} />

                {/* Concept detail deep link only — not primary nav.
                    Static knowledge/sources must outrank :id. */}
                <Route
                  path="research/knowledge/sources"
                  element={<Navigate to="/admin/research/papers" replace />}
                />
                <Route
                  path="research/knowledge"
                  element={<Navigate to="/admin/research/papers" replace />}
                />
                <Route path="research/knowledge/:id" element={<ResearchConceptDetailPage />} />
                <Route path="research/gaps/:id" element={<ResearchGapDetailPage />} />

                {/* --- Legacy redirects --- */}
                <Route
                  path="research/discovery"
                  element={<Navigate to="/admin/research/research/inbox" replace />}
                />
                <Route
                  path="research/discovery/import"
                  element={<Navigate to="/admin/research/papers/import" replace />}
                />
                <Route
                  path="research/discovery/topics"
                  element={<Navigate to="/admin/research/research/programs" replace />}
                />
                <Route
                  path="research/discovery/settings"
                  element={<Navigate to="/admin/research/research/settings" replace />}
                />
                <Route
                  path="research/discovery/runs"
                  element={<Navigate to="/admin/research/research/activity" replace />}
                />
                <Route
                  path="research/discovery/sources"
                  element={<Navigate to="/admin/research/papers" replace />}
                />
                <Route
                  path="research/sources"
                  element={<Navigate to="/admin/research/papers" replace />}
                />
                <Route
                  path="research/sources/:id"
                  element={<ResearchRedirect to={(p) => `/admin/research/papers/${p.id}`} />}
                />
                <Route
                  path="research/sources/:id/validate/:pass"
                  element={
                    <ResearchRedirect to={(p) => `/admin/research/papers/${p.id}?stage=validate`} />
                  }
                />
                <Route
                  path="research/evidence"
                  element={<Navigate to="/admin/research/papers" replace />}
                />
                <Route
                  path="research/evidence/import"
                  element={<Navigate to="/admin/research/papers/import" replace />}
                />
                <Route
                  path="research/evidence/:id"
                  element={<ResearchRedirect to={(p) => `/admin/research/papers/${p.id}`} />}
                />
                <Route
                  path="research/evidence/:id/validate/:pass"
                  element={
                    <ResearchRedirect to={(p) => `/admin/research/papers/${p.id}?stage=validate`} />
                  }
                />
                <Route
                  path="research/hypotheses"
                  element={<Navigate to="/admin/research/questions" replace />}
                />
                <Route
                  path="research/hypotheses/:id"
                  element={<ResearchRedirect to={(p) => `/admin/research/questions/${p.id}`} />}
                />
                <Route
                  path="research/concepts"
                  element={<Navigate to="/admin/research/papers" replace />}
                />
                <Route
                  path="research/concepts/:id"
                  element={
                    <ResearchRedirect
                      to={(p) => `/admin/research/knowledge/${encodeURIComponent(p.id ?? '')}`}
                    />
                  }
                />
                <Route
                  path="research/lina-evidence"
                  element={<Navigate to="/admin/research/papers?type=lina-evidence" replace />}
                />
                <Route
                  path="research/findings"
                  element={<Navigate to="/admin/research/claims" replace />}
                />
                <Route
                  path="research/gaps"
                  element={<Navigate to="/admin/research/questions" replace />}
                />
                <Route
                  path="research/graph"
                  element={<Navigate to="/admin/research/papers" replace />}
                />
                <Route
                  path="research/projects"
                  element={<Navigate to="/admin/research" replace />}
                />
                <Route
                  path="research/projects/:id"
                  element={<Navigate to="/admin/research" replace />}
                />
                <Route
                  path="research/workspaces"
                  element={<Navigate to="/admin/research/questions" replace />}
                />
                <Route
                  path="research/workspaces/:id"
                  element={<Navigate to="/admin/research/questions" replace />}
                />

                {/* Communication Preference Study */}
                <Route path="study" element={<StudyDashboardAdminPage />} />
                <Route path="study/examples" element={<StudyExamplesAdminPage />} />
                <Route
                  path="study/examples/new"
                  element={<Navigate to="/admin/study/examples?create=1" replace />}
                />
                <Route path="study/examples/:id" element={<StudyExampleEditorAdminPage />} />
                <Route path="study/analytics" element={<StudyAnalyticsAdminPage />} />
                <Route path="study/lab" element={<Navigate to="/admin/study/examples" replace />} />
              </Route>

              {/* --- Error Pages (can be accessed directly) --- */}
              <Route path="/error/404" element={<NotFoundPage />} />
              <Route path="/error/500" element={<ServerErrorPage />} />
              <Route path="/error/account" element={<AccountErrorPage />} />
              <Route path="/error/maintenance" element={<MaintenancePage />} />

              {/* --- Fallback Route (404) --- */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </div>
        </Suspense>
      </main>
      <SonnerToaster position="top-center" richColors />
      <Toaster />
    </TooltipProvider>
  )
}

// --- Header Wrapper Component ---
const HeaderWrapper = () => {
  const location = useLocation()
  const { accountData } = useAuth()
  const isStandaloneRoute = isStandaloneSurface(location.pathname)

  // Extract currentStep from location state for analyzer page
  const currentStep = (location.state as any)?.currentStep

  // Handler for restart button on analyzer pages
  const handleStartOver = () => {
    if (isAnalyzerPage(location.pathname)) {
      // Use centralized restart function
      restartAnalyzer()
    }
  }

  // Don't show header on standalone routes (auth, lens, extension)
  if (isStandaloneRoute) {
    return null
  }

  return (
    <AppHeader
      currentStep={currentStep}
      isAdmin={accountData?.is_admin === true}
      showMenuButton={true}
      onStartOver={handleStartOver}
    />
  )
}

// --- Footer Wrapper Component ---
const FooterWrapper = () => {
  const location = useLocation()
  const isStandaloneRoute = isStandaloneSurface(location.pathname)

  return !isStandaloneRoute ? <AppFooter /> : null
}

const AdminPartnersLegacyRedirect = () => {
  const { orgId } = useParams()
  return <Navigate to={orgId ? `/admin/organizations/${orgId}` : '/admin/organizations'} replace />
}

// --- Admin Toolbar Wrapper Component (Admin Only) ---
const AdminToolbarWrapper = () => {
  const { accountData, isAccountLoading } = useAuth()
  const location = useLocation()
  const isStandaloneRoute = isStandaloneSurface(location.pathname)

  // Only show for admin users (use accountData to ensure it shows regardless of QA perspective)
  // and not on standalone routes
  const isAdmin = accountData?.is_admin === true

  // Don't render during loading or on standalone routes or if not admin
  if (isAccountLoading || isStandaloneRoute || !isAdmin) {
    return null
  }

  return <AdminToolbar />
}

// --- Accessibility Toolbar Wrapper Component (All Users) ---
const AccessibilityToolbarWrapper = () => {
  const location = useLocation()
  const isStandaloneRoute = isStandaloneSurface(location.pathname)

  if (isStandaloneRoute) {
    return null
  }

  return <AccessibilityToolbar />
}

// --- Floating TTS Toolbar Wrapper Component ---
const FloatingTTSToolbarWrapper = () => {
  const location = useLocation()
  const isStandaloneRoute = isStandaloneSurface(location.pathname)

  if (isStandaloneRoute) {
    return null
  }

  return <FloatingTTSToolbar />
}

// --- Support Chat Wrapper (hidden on standalone Lens / auth surfaces) ---
const SupportChatWrapper = () => {
  const location = useLocation()
  const isStandaloneRoute = isStandaloneSurface(location.pathname)

  if (isStandaloneRoute) {
    return null
  }

  return <SupportChat />
}

// --- Route help button (knowledge-graph documented-by articles) ---
const RouteHelpWrapper = () => {
  const location = useLocation()
  const isStandaloneRoute = isStandaloneSurface(location.pathname)

  if (isStandaloneRoute) {
    return null
  }

  return <HelpButton />
}

// --- Main App Component ---
const App = () => {
  // Initialize storage system on app load
  useEffect(() => {
    initializeStorage()
  }, [])

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
      },
    },
  })

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ErrorBoundary
          FallbackComponent={ErrorFallback}
          onError={(error, info) => {
            const errorMessage = error instanceof Error ? error.message : ''
            // Ignore navigation/auth errors - these are transient
            const isNavigationError =
              errorMessage.includes('Navigation') ||
              errorMessage.includes('Cannot update during render') ||
              errorMessage.includes('useNavigate') ||
              errorMessage.includes('useLocation')

            if (!isNavigationError) {
              console.error('Error Boundary caught error:', error, info)
            }
          }}
          onReset={() => {
            // Clear any error state when resetting
            window.location.reload()
          }}
        >
          <Router>
            <AuthProvider>
              <ActiveContextProvider>
                <AdminToolbarProvider>
                  <QASessionProvider enabled={true}>
                    <QAPerspectiveProvider>
                      <AccessibilityProvider>
                        <TextToSpeechProvider>
                          <ReadAloudProvider>
                            <QAPerspectiveLabel />
                            <AppShell>
                              <HeaderWrapper />
                              <AppContent />
                              <FooterWrapper />
                            </AppShell>
                            <AdminToolbarWrapper />
                            <AccessibilityToolbarWrapper />
                            <FloatingTTSToolbarWrapper />
                            <SupportChatWrapper />
                            <RouteHelpWrapper />
                            {/* <PasskeyEnrollmentGate /> */}
                          </ReadAloudProvider>
                        </TextToSpeechProvider>
                      </AccessibilityProvider>
                    </QAPerspectiveProvider>
                  </QASessionProvider>
                </AdminToolbarProvider>
              </ActiveContextProvider>
            </AuthProvider>
          </Router>
        </ErrorBoundary>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

export default App
